import { useState, useEffect, useCallback } from 'react';
import { listConferenceRooms, getConferenceRoomAvailability } from 'services/Settings';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { combineDateAndTime } from 'utils/dateTimeUtils';

export const useConferenceRoomAvailability = (
  selectedLocation,
  meetingStartTime,
  meetingEndTime,
  options = {},
  excludeEventId = null,
  selectedDate = null
) => {
  const [conferenceRooms, setConferenceRooms] = useState([]);
  const [roomAvailability, setRoomAvailability] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
  const [error, setError] = useState(null);
  const { userDetails } = useUserDetailsContext();

  // Options to control behavior
  const {
    autoCheckAvailability = false, // Only check availability automatically if explicitly enabled
    checkOnTimeChange = false, // Only check when time changes if explicitly enabled
    isAllDay = false, // Whether the event is all-day
  } = options;

  // Fetch conference rooms for the selected location
  const fetchConferenceRooms = useCallback(async () => {
    if (!selectedLocation) {
      setConferenceRooms([]);
      setRoomAvailability({});
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Fetch both conference rooms and office locations
      const roomsResponse = await listConferenceRooms(selectedLocation);

      const rooms = roomsResponse?.data || [];

      // Filter rooms for the selected location and add office names
      const filteredRooms = rooms
        .filter((room) => String(room?.office_location_id) === String(selectedLocation))
        .map((room) => ({
          ...room,
          label: room?.room_name || room?.name,
          value: room?.conference_room_id || room?.room_id || room?.id,
        }));

      setConferenceRooms(filteredRooms);
    } catch (err) {
      console.error('Error fetching conference rooms:', err);
      setError('Failed to load conference rooms');
      setConferenceRooms([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedLocation]);

  // Check availability for all rooms
  const checkRoomAvailability = useCallback(async () => {
    if (!selectedLocation || !meetingStartTime || !meetingEndTime || conferenceRooms.length === 0) {
      setRoomAvailability({});
      return;
    }

    setIsCheckingAvailability(true);
    setError(null);

    try {
      const conferenceRoomIds = conferenceRooms.map((room) => room.value);

      // Use the utility function to combine date and time
      const { formattedStartTime, formattedEndTime } = combineDateAndTime({
        selectedDate,
        meetingStartTime,
        meetingEndTime,
        isAllDay,
        timezone: userDetails?.timezone,
      });

      const availabilityData = {
        office_location_id: selectedLocation,
        meeting_start_time: formattedStartTime,
        meeting_end_time: formattedEndTime,
        conference_room_list: conferenceRoomIds,
      };

      // Add exclude_event_id if provided (for edit mode)
      if (excludeEventId) {
        availabilityData.exclude_event_id = excludeEventId;
      }

      const availabilityResponse = await getConferenceRoomAvailability(availabilityData);
      const availabilityResults = availabilityResponse?.data || {};

      // Create availability map from the response object
      const availabilityMap = {};
      conferenceRoomIds.forEach((roomId) => {
        // Check if the room exists in response and get its availability status
        const roomAvailability = availabilityResults[roomId];
        availabilityMap[roomId] = roomAvailability?.available === true;
      });

      setRoomAvailability(availabilityMap);
    } catch (err) {
      console.error('Error checking room availability:', err);
      setError('Failed to check room availability');
      // Set all rooms as unavailable if availability check fails
      // This prevents users from selecting potentially booked rooms
      const fallbackAvailability = {};
      conferenceRooms.forEach((room) => {
        fallbackAvailability[room.value] = false;
      });
      setRoomAvailability(fallbackAvailability);
    } finally {
      setIsCheckingAvailability(false);
    }
  }, [selectedLocation, meetingStartTime, meetingEndTime, conferenceRooms, excludeEventId, isAllDay, selectedDate, userDetails?.timezone]);

  // Fetch rooms when location changes
  useEffect(() => {
    fetchConferenceRooms();
  }, [fetchConferenceRooms]);

  // Check availability when rooms are loaded (only if auto-check is enabled)
  useEffect(() => {
    if (conferenceRooms.length > 0 && autoCheckAvailability) {
      checkRoomAvailability();
    }
  }, [checkRoomAvailability, conferenceRooms.length, autoCheckAvailability]);

  // Check availability when time changes (only if explicitly enabled)
  useEffect(() => {
    if (checkOnTimeChange && conferenceRooms.length > 0 && meetingStartTime && meetingEndTime) {
      checkRoomAvailability();
    }
  }, [checkOnTimeChange, meetingStartTime, meetingEndTime, checkRoomAvailability, conferenceRooms.length]);

  // Generate room options with availability status
  const roomOptionsWithAvailability = conferenceRooms.map((room) => {
    const isAvailable = roomAvailability[room.value];
    const hasAvailabilityData = roomAvailability.hasOwnProperty(room.value);

    const capacityText = room.capacity ? ` (${room.capacity})` : '';

    // Show availability status in dropdown
    let availabilityText = '';
    if (hasAvailabilityData) {
      availabilityText = isAvailable ? ' (Available)' : ' (Unavailable)';
    } else {
      availabilityText = ' (Checking...)';
    }

    return {
      ...room,
      label: `${room.label}${capacityText}`, // Clean label for selected value display
      dropdownLabel: `${room.label}${capacityText}${availabilityText}`, // Full label with availability for dropdown
      isAvailable: isAvailable,
      disabled: hasAvailabilityData && isAvailable === false, // Disable unavailable rooms
      hasAvailabilityData: hasAvailabilityData,
    };
  });

  return {
    conferenceRooms: roomOptionsWithAvailability,
    isLoading: isLoading || isCheckingAvailability, // Show loading when fetching rooms OR checking availability
    isCheckingAvailability,
    error,
    refreshAvailability: checkRoomAvailability,
  };
};
